import { Card, CardContent } from '../../ui/card';
import { Phone, MessageSquare, Activity, LucideIcon } from 'lucide-react';
import type { CallChannel, CallVolumeChannelData } from '../../../services/api';
import { CallVolumeChart } from './CallVolumeChart';
import { LABELS } from './labels';

interface CallVolumeTabProps {
  channel: CallChannel;
  data: CallVolumeChannelData;
}

interface ChannelMeta {
  icon: LucideIcon;
  /** Bar colour for the per-day count chart. */
  color: string;
  /** Icon container background. */
  bgColor: string;
  /** Icon colour. */
  textColor: string;
}

const CHANNEL_META: Record<CallChannel, ChannelMeta> = {
  voice: { icon: Phone, color: '#2563eb', bgColor: 'bg-blue-50', textColor: 'text-blue-600' },
  chat: { icon: MessageSquare, color: '#16a34a', bgColor: 'bg-green-50', textColor: 'text-green-600' },
  combined: { icon: Activity, color: '#9333ea', bgColor: 'bg-purple-50', textColor: 'text-purple-600' },
};

/** Peak-parallel bars share one accent colour across channels to signal the metric. */
const MAX_PARALLEL_COLOR = '#ea580c'; // orange-600

/**
 * Renders the three CA-586 metrics for a single channel: total in month,
 * count per day, and peak parallel sessions per day.
 */
export function CallVolumeTab({ channel, data }: CallVolumeTabProps) {
  const meta = CHANNEL_META[channel];
  const Icon = meta.icon;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${meta.bgColor}`}>
              <Icon className={`w-5 h-5 ${meta.textColor}`} />
            </div>
            <div>
              <p className="text-sm text-gray-600">{LABELS.metrics.total[channel]}</p>
              <p className="text-2xl font-bold text-gray-900">{data.total.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <CallVolumeChart
        title={LABELS.metrics.perDay[channel]}
        data={data.days}
        dataKey="count"
        color={meta.color}
      />
      <CallVolumeChart
        title={LABELS.metrics.maxParallel[channel]}
        data={data.days}
        dataKey="maxParallel"
        color={MAX_PARALLEL_COLOR}
      />
    </div>
  );
}
